/**
 * InviteAccept — public tokenized invite landing page
 *
 * Route: /invite/:token  (no auth required)
 * Pulls the invitation summary (org + role + inviter). If expired or
 * already used, shows a friendly dead-end message. Otherwise collects
 * first/last/password and POSTs to /accept. On success the server sets
 * auth cookies and we jump straight to /dashboard.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle2, XCircle, Trophy, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/invitations/token/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(e => setLoadErr(e.response?.data?.detail || 'Invitation not found'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.password.length < 8) return setErr('Password must be at least 8 characters');
    if (form.password !== form.confirm) return setErr('Passwords do not match');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/invitations/token/${token}/accept`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
      });
      window.location.href = '/dashboard';
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not accept invitation');
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <DeadEnd title="Invitation unavailable" message={loadErr} onHome={() => navigate('/login')} />
    );
  }
  if (!invite) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (invite.status !== 'pending') {
    const titles = {
      accepted: 'Invitation already accepted',
      expired:  'Invitation expired',
      revoked:  'Invitation revoked',
    };
    return (
      <DeadEnd
        title={titles[invite.status] || 'Invitation unavailable'}
        message={
          invite.status === 'accepted' ? 'This invite has already been used. Please sign in with your credentials.' :
          invite.status === 'expired'  ? 'Ask your admin to send you a new invitation.' :
          invite.status === 'revoked'  ? 'This invitation was revoked by an administrator.' :
          'Please contact support.'
        }
        onHome={() => navigate('/login')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black text-slate-100 flex items-center justify-center px-6 py-10" data-testid="invite-accept-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="h-6 w-6 text-amber-500" />
          <div>
            <h1 className="text-2xl font-semibold">Accept your invitation</h1>
            <p className="text-sm text-slate-400">Create your login to join <strong className="text-slate-200">{invite.org_name}</strong>.</p>
          </div>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge className="bg-slate-800 text-slate-300">{invite.email}</Badge>
              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30">Role: {invite.role_label}</Badge>
              <Badge className="bg-slate-800 text-slate-300">invited by {invite.invited_by_name}</Badge>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-400">First name</Label>
                  <Input required value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))}
                    className="bg-slate-950 border-slate-800 text-slate-100 h-11 mt-1" data-testid="invite-first-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Last name</Label>
                  <Input required value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))}
                    className="bg-slate-950 border-slate-800 text-slate-100 h-11 mt-1" data-testid="invite-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-400">Password</Label>
                  <Input type="password" minLength={8} required value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    className="bg-slate-950 border-slate-800 text-slate-100 h-11 mt-1" data-testid="invite-password" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Confirm</Label>
                  <Input type="password" minLength={8} required value={form.confirm} onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))}
                    className="bg-slate-950 border-slate-800 text-slate-100 h-11 mt-1" data-testid="invite-confirm" />
                </div>
              </div>

              {err && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="invite-error">
                  {err}
                </div>
              )}

              <Button type="submit" disabled={submitting}
                className="w-full h-12 bg-[#1e3a8a] hover:bg-[#1e40af] text-white tracking-[0.15em] text-sm"
                data-testid="invite-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>ACCEPT & CREATE ACCOUNT <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DeadEnd({ title, message, onHome }) {
  return (
    <div className="min-h-screen bg-black text-slate-100 flex items-center justify-center px-6" data-testid="invite-dead-end">
      <Card className="bg-slate-900 border-slate-800 max-w-md w-full">
        <CardContent className="p-8 text-center space-y-4">
          <XCircle className="h-10 w-10 text-red-500 mx-auto" />
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-sm text-slate-400">{message}</p>
          <Button onClick={onHome} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Go to sign in
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
